/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {performExport} from '@ion/applications/src/store/actions/exporter';
import {adjustSortField} from '@ion/applications/src/store/helpers';
import {error} from '@ion/core/src/logger/logger';
import {getCVSTrackingAssets, loadAllGeofenceEvents, loadGeofenceEventsCount} from '@ion/core/src/services/ArcGIS';
import {CVSQueryArgs} from '@ion/core/src/services/ArcGISUtils';
import {
  loadAllAccounts,
  loadAllAssets,
  loadAllDevices,
  loadAllIOProfiles,
  loadAllNotifications,
  loadAllOperators,
  loadAllRoles,
  loadAllSchedules,
  loadAllUsers
} from '@ion/core/src/services/Connect';
import {
  loadAllMaintenanceDashboardItems,
  loadAllMaintenanceSchedules,
  loadAllServiceItems
} from '@ion/core/src/services/Maintenance';
import {CTCEntity, GenericObject, Roles} from '@ion/core/src/types/entities';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {downloadBlankTemplate} from '@ion/core/src/xlsx/TemplateHelpers';
import {
  ExportClient,
  exportCSV,
  ExportNonPaginatedClient,
  ExportXLSType,
  generateRows,
  paging
} from '@ion/dls/src/grid/Exporter';
import {getAdditionalFilterParams} from '@/routes/tracking/trackingUtils';
import {
  getAccountId,
  getAllAccountIds,
  getCheckedAccountIds,
  getFilteredAssets,
  getFilterRequestParams,
  resolveAssetDeviceIdentifiers
} from '../helpers';
import {ExportContext} from './../../types/redux';

/**
 * defines the shape of the report export paramaters which are generally used for
 * export the report grid.
 */
export interface ExportReport extends ExportClient {
  items: CTCEntity[];
  title?: string;
  subTitle?: string;
  augmenterArgs?: any[] | GenericObject;
}

// invoked when an export is initiated ...
export const EXPORT_REQUEST   = 'EXPORT_REQUEST';

// invoked when an export has completed ...
export const EXPORT_RESPONSE  = 'EXPORT_RESPONSE';

// invoked when an export has failed ...
export const EXPORT_ERROR     = 'EXPORT_ERROR';

// invoked when the user has been notified of an initiated export ...
export const EXPORT_CONFIRMED = 'EXPORT_CONFIRMED';

// invoked when the user has been notified of a completed export ...
export const EXPORT_COMPLETED = 'EXPORT_COMPLETED';

/**
 * General utility which helps in downloading templates as excel with pre-populated
 * values specific to current CTC Entity type.
 */
export const exportXLS = ({
  entity,
  items,
  columns,
  augmenter,
  augmenterArgs,
  params
}: ExportXLSType) => {
  const rows: CTCEntity[] = generateRows({
    entity,
    items,
    columns,
    augmenter,
    augmenterArgs
  });
  params.rows = rows;
  downloadBlankTemplate({...params});
};

/**
 * Provides the Asset Export Action Creator which manages retrieving all assets
 * and converting the retrieved assets to a CSV for subsequent download.
 */
export const exportAssets = (
  {columns, augmenter, requestValidations, ...rest}: ExportClient,
  downloadParams?: DownloadTemplate) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.asset;
    dispatch({type: EXPORT_REQUEST, entity});
    const {valid} = requestValidations;
    try {
      const accountId = getAccountId(getState);
      adjustSortField(rest, 'name');
      let items = valid ? await loadAllAssets({accountId, ...rest, ...paging}) : [];
      // the Asset import template exposes a distinct column per device
      // identifier, which requires resolving each paired device beyond the
      // links returned by the Assets search ...
      if (downloadParams) {
        items = await resolveAssetDeviceIdentifiers(items, accountId, rest?.includeSubGroups as boolean);
      }
      performExport({
        exportItems : () => {
          const exportParams = {entity, items, columns, augmenter};
          if (!downloadParams) {
            exportCSV({...exportParams, includeTimestamp: true});
          } else {
            exportXLS({...exportParams, params: downloadParams});
          }
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        dispatch,
        entity,
        items
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Device Export Action Creator which manages retrieving all devices
 * and converting the retrieved devices to a CSV for subsequent download.
 */
export const exportDevices = ({columns, augmenter, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.device;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const accountId = getAccountId(getState);
      const items = await loadAllDevices({accountId, ...rest, ...paging});
      exportCSV({
        includeTimestamp : true,
        entity,
        items,
        columns,
        augmenter
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Driver Export Action Creator which manages retrieving all drivers
 * and converting the retrieved drivers to a CSV for subsequent download.
 */
export const exportDrivers = (
  {columns, augmenter, requestValidations, ...rest}: ExportClient,
  downloadParams?: DownloadTemplate) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.driver;
    dispatch({type: EXPORT_REQUEST, entity});
    const {valid} = requestValidations;
    try {
      const accountId    = getAccountId(getState);
      const items        = valid ? await loadAllOperators({accountId, ...rest, ...paging}) : [];
      performExport({
        exportItems : () => {
          const exportParams = {entity, items, columns, augmenter};
          if (!downloadParams) {
            exportCSV({...exportParams, includeTimestamp: true});
          } else {
            exportXLS({...exportParams, params: downloadParams});
          }
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        entity,
        dispatch,
        items
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Groups Export Action Creator which manages retrieving all groups
 * and converting the retrieved groups to a CSV for subsequent download.
 */
export const exportGroups = (
  {columns, augmenter, requestValidations, includeSubGroups, ...rest}: ExportClient,
  downloadParams?: DownloadTemplate) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.group;
    dispatch({type: EXPORT_REQUEST, entity});
    const {valid} = requestValidations;
    try {
      const parentAccountId      = getAccountId(getState);
      const items                = valid ? await loadAllAccounts({
        subAccountIds : includeSubGroups
          ? getAllAccountIds(getState, parentAccountId, true).toString()
          : null,
        parentAccountId,
        includeSubGroups,
        groupSearchKey : 'parent',
        ...rest,
        ...paging
      }) : [];
      performExport({
        exportItems : () => {
          const augmenterArgs: [any] = [true];
          const exportParams         = {entity, items, columns, augmenter, augmenterArgs};
          if (!downloadParams) {
            exportCSV({...exportParams, includeTimestamp: true});
          } else {
            exportXLS({...exportParams, params: downloadParams});
          }
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        entity,
        dispatch,
        items
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Users Export Action Creator which manages retrieving all users
 * and converting the retrieved users to a CSV for subsequent download.
 */
export const exportUsers = (
  {columns, augmenter, requestValidations, ...rest}: ExportClient,
  downloadParams?: DownloadTemplate) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.user;
    dispatch({type: EXPORT_REQUEST, entity});
    const {valid} = requestValidations;
    try {
      const accountId = getAccountId(getState);
      adjustSortField(rest, 'username');
      const items     = valid ? await loadAllUsers({accountId, ...rest, ...paging}) : [];
      performExport({
        exportItems : () => {
          const roles                  = getState().adminUsers.roles;
          const augmenterArgs: [Roles] = [roles];
          const exportParams           = {entity, items, columns, augmenter, augmenterArgs};
          if (!downloadParams) {
            exportCSV({...exportParams, includeTimestamp: true});
          } else {
            exportXLS({...exportParams, params: downloadParams});
          }
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        entity,
        items,
        dispatch
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Roles Export Action Creator which manages retrieving all roles
 * and converting the retrieved roles to a CSV for subsequent download.
 */
export const exportRoles = ({columns, augmenter, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.role;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const accountId = getAccountId(getState);
      const items = await loadAllRoles({accountId, ...rest, ...paging});
      exportCSV({
        includeTimestamp : true,
        entity,
        items,
        columns,
        augmenter
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Schedules Export Action Creator which manages retrieving all schedules
 * and converting the retrieved schedules to a CSV for subsequent download.
 */
export const exportSchedules = ({columns, augmenter, requestValidations, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.schedule;
    dispatch({type: EXPORT_REQUEST, entity});
    const {valid} = requestValidations;
    try {
      const accountId = getAccountId(getState);
      adjustSortField(rest, 'name');
      const items = valid ? await loadAllSchedules({accountId, ...rest, ...paging}) : [];
      performExport({
        exportItems : () => {
          exportCSV({
            augmenterArgs    : [true],
            includeTimestamp : true,
            entity,
            items,
            columns,
            augmenter
          });
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        items,
        entity,
        dispatch
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the IOProfile Export Action Creator which manages retrieving all ioprofiles
 * and converting the retrieved ioprofiles to a CSV for subsequent download.
 */
export const exportIOProfiles = ({columns, augmenter, requestValidations, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.ioProfile;
    dispatch({type: EXPORT_REQUEST, entity});
    const {valid} = requestValidations;
    try {
      const accountId = getAccountId(getState);
      adjustSortField(rest, 'profileName');
      const items = valid ? await loadAllIOProfiles({accountId, ...rest, ...paging}) : [];
      performExport({
        exportItems : () => {
          exportCSV({
            augmenterArgs    : [true],
            includeTimestamp : true,
            entity,
            items,
            columns,
            augmenter
          });
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        items,
        dispatch,
        entity
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Notification Export Action Creator which manages retrieving all Notifications
 * and converting the retrieved Notifications to a CSV for subsequent download.
 */
export const exportNotifications = ({columns, augmenter, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.notification;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      const accountId = getAccountId(getState);
      const items = await loadAllNotifications({accountId, ...rest, ...paging});
      exportCSV({
        augmenterArgs    : [true],
        includeTimestamp : true,
        entity,
        items,
        columns,
        augmenter
      });
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Maintenance Service Schedule Export Action Creator which manages retrieving
 * all Maintenance Service Schedules and converting the retrieved Schedules to a CSV for
 * subsequent download.
 */
export const exportMaintenanceServiceSchedules = (
  {columns, augmenter, includeSubGroups, filterOn, sortOn, requestValidations, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.serviceSchedule;
    dispatch({type: EXPORT_REQUEST, entity});
    const {valid} = requestValidations;
    try {
      const items = valid ? await loadAllMaintenanceSchedules({
        accountId : getAccountId(getState),
        hierarchy : includeSubGroups,
        filters   : filterOn,
        sort      : sortOn,
        ...paging,
        ...rest
      }) : [];
      performExport({
        exportItems : () => {
          const accountsMap = getState().globalGroup.accountMap;
          exportCSV({
            augmenterArgs    : [{accountsMap}],
            includeTimestamp : true,
            entity,
            items,
            columns,
            augmenter
          });
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        items,
        entity,
        dispatch
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Maintenance Service Item Export Action Creator which manages retrieving
 * all Service Items and converting the retrieved Schedules to a CSV for subsequent
 * download.
 */
export const exportMaintenanceServiceItems = (
  {columns, augmenter, includeSubGroups, filterOn, sortOn, requestValidations, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.serviceItem;
    dispatch({type: EXPORT_REQUEST, entity});
    const {valid} = requestValidations;
    try {
      const items = valid ? await loadAllServiceItems({
        accountId    : getAccountId(getState),
        hierarchy    : includeSubGroups,
        filters      : filterOn,
        sort         : sortOn,
        convertUnits : true,
        ...paging,
        ...rest
      }) : [];
      performExport({
        exportItems : () => {
          const accountsMap = getState().globalGroup.accountMap;
          exportCSV({
            augmenterArgs    : [{accountsMap}],
            includeTimestamp : true,
            entity,
            items,
            columns,
            augmenter
          });
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        items,
        dispatch,
        entity
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Maintenance Export Action Creators
 */

/**
 * Provides the Maintenance Dashboard Export Action Creator which manages retrieving
 * all Dashboard Items and converting the retrieved Items to a CSV for subsequent
 * download.
 */
export const exportMaintenanceDashboard = (
  {columns, augmenter, includeSubGroups, requestValidations, filterOn, sortOn, ...rest}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.maintenanceDashboard;
    dispatch({type: EXPORT_REQUEST, entity});
    const {valid} = requestValidations;
    try {
      const items = valid ? await loadAllMaintenanceDashboardItems({
        accountId    : getAccountId(getState),
        hierarchy    : includeSubGroups,
        filterOn     : filterOn || {},
        sortOn       : sortOn ? sortOn[0] : sortOn,
        convertUnits : true,
        ...paging,
        ...rest
      }) : [];
      performExport({
        exportItems : () => {
          const accountsMap = getState().globalGroup.accountMap;
          exportCSV({
            augmenterArgs    : [{accountsMap}],
            includeTimestamp : true,
            entity,
            items,
            columns,
            augmenter
          });
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        entity,
        items,
        dispatch
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Maintenance Schedules Export Action Creator which manages retrieving
 * all Schedule Items and converting the retrieved Items to a CSV for subsequent
 * download.
 */
export const exportMaintenanceSchedules = (
  {columns, items}: ExportNonPaginatedClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.maintenanceSchedule;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      performExport({
        exportItems : () => {
          exportCSV({
            includeTimestamp : true,
            entity,
            items,
            columns
          });
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        entity,
        items,
        dispatch
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Maintenance History Export Action Creator which manages retrieving
 * all History Items and converting the retrieved Items to a CSV for subsequent
 * download.
 */
export const exportMaintenanceHistory = (
  {columns, items}: ExportNonPaginatedClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.maintenanceHistory;
    dispatch({type: EXPORT_REQUEST, entity});
    try {
      performExport({
        exportItems : () => {
          exportCSV({
            includeTimestamp : true,
            entity,
            items,
            columns
          });
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        items,
        entity,
        dispatch
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Asset History Export Action Creator which manages passed
 * all History Items and converting the retrieved Items to a CSV for subsequent
 * download.
 */
export const exportAssetHistoryReport = (
  {columns, items, title, subTitle, augmenter, requestValidations, augmenterArgs}: ExportReport) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.assetHistory;
    dispatch({type: EXPORT_REQUEST, entity});
    const {valid} = requestValidations;
    try {
      performExport({
        exportItems : () => {
          exportCSV({
            title,
            subTitle,
            columns,
            entity,
            augmenter,
            items,
            augmenterArgs,
            includeTimestamp : true
          });
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        items : (valid && items) || [],
        entity,
        dispatch
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Shipment History Export Action Creator which manages passed
 * all History Items and converting the retrieved Items to a CSV for subsequent
 * download.
 */
export const exportShipmentHistoryReport = ({
  columns,
  items,
  title,
  subTitle,
  augmenter,
  augmenterArgs,
  requestValidations
}: ExportReport) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.shipmentHistory;
    dispatch({type: EXPORT_REQUEST, entity});
    const {valid} = requestValidations;
    try {
      performExport({
        exportItems : () => {
          exportCSV({
            includeTimestamp : true,
            title,
            subTitle,
            columns,
            entity,
            augmenter,
            items,
            augmenterArgs
          });
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        items : (valid && items) || [],
        entity,
        dispatch
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Tracking Assets Export Action Creator which manages retrieving
 * all Tracking Asset Items and converting the retrieved Items to a CSV for
 * subsequent download.
 */
export const exportTrackingAssets = ({
  columns,
  augmenter,
  sortOn,
  filterOn,
  items,
  largeFleetAssetCount,
  requestValidations,
  includeSciDevice = false
}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.assetTracking;
    dispatch({type: EXPORT_REQUEST, entity});
    const {valid} = requestValidations;
    try {
      if (!items && valid) {
        const {
          tracking: {
            filters: {
              assetTypes,
              assetCategories,
              geoFences,
              routes,
              assetStatus,
              assets: {
                selected: assetsSelected
              }
            }
          }
        } = getState();
        const groupIds                         = getCheckedAccountIds(getState);
        const {statusFilter, addressFilterObj} = getAdditionalFilterParams(filterOn);
        const assetStatusParams                = [...getFilterRequestParams(assetStatus.selected)];
        if (statusFilter?.length) {
          Array.isArray(statusFilter)
            ? assetStatusParams.push(...statusFilter)
            : assetStatusParams.push(statusFilter);
        }
        const params: CVSQueryArgs = {
          groups          : groupIds,
          assets          : getFilteredAssets(assetsSelected, groupIds),
          assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
          assetCategories : [...getFilterRequestParams(assetCategories.selected)],
          geoFences       : [...getFilterRequestParams(geoFences.selected)],
          routes          : [...getFilterRequestParams(routes.selected)],
          assetStatus     : assetStatusParams,
          ...addressFilterObj,
          includeSciDevice
        };

        if (sortOn) {
          params.sort = sortOn;
        }

        if (filterOn) {
          params.filter = filterOn;
        }
        const items = await getCVSTrackingAssets(params, largeFleetAssetCount);
        performExport({
          exportItems : () => {
            exportCSV({
              augmenterArgs    : [true],
              includeTimestamp : true,
              entity,
              items,
              columns,
              augmenter
            });
          },
          items,
          entity,
          dispatch
        });
      } else {
        performExport({
          exportItems : () => {
            exportCSV({
              augmenterArgs    : [true],
              includeTimestamp : true,
              entity,
              items,
              columns,
              augmenter
            });
          },
          items : (valid && items) || [],
          entity,
          dispatch
        });
      }
      dispatch({type: EXPORT_RESPONSE, entity});
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Geofence Events Export Action Creator which manages retrieving all geofence
 * events and converting the retrieved events to a CSV for subsequent download.
 */
export const exportGeofenceEvents = ({
  columns,
  sortOn,
  augmenter,
  requestValidations,
  pageSize,
  ...rest
}: ExportClient) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = ExportContext.geofenceEvents;
    dispatch({type: EXPORT_REQUEST, entity});
    const {valid} = requestValidations;
    try {
      const accountId = getAccountId(getState);
      const count     = valid ? await loadGeofenceEventsCount({
        accountId,
        sortOn : sortOn?.[0],
        ...rest
      }) : 0;
      const items = valid ? await loadAllGeofenceEvents({
        accountId,
        sortOn   : sortOn?.[0],
        pageId   : 1,
        pageSize : count,
        ...rest
      }) : [];
      performExport({
        exportItems : () => {
          exportCSV({
            includeTimestamp : true,
            entity,
            items,
            columns,
            augmenter
          });
          dispatch({type: EXPORT_RESPONSE, entity});
        },
        items,
        entity,
        dispatch
      });
    } catch (err) {
      dispatch({type: EXPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Invoked when an export has completed and the user confirms acknowledgement.
 */
export const exportConfirmed = () => ({
  type : EXPORT_CONFIRMED
});

/**
 * Invoked when an export has completed and the user confirms acknowledgement.
 */
export const exportComplete = () => ({
  type : EXPORT_COMPLETED
});
